// FORM: https://github.com/modusign/modusign-client/blob/35ac05fa1f9337ff1aeacd765766a3b36653b583/app/src/components/TextField/ConfigurableTextField/index.tsx

import { Box } from '@modusign/design-system';
import { useAddressField } from '@modusign/react-signing';
import { cx } from 'emotion';
import { useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle, useUpdateEffect } from 'react-use';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getAddressFieldCorrectedSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getAddressFieldCorrectedSize';
import { getSignerDisplayName } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getSignerDisplayName';
import { getTextBaseFieldMinimumSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldMinimumSize';
import { ConfigurableAutofillFieldGroupRendererProps } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/AutofillFieldGroupRenderer';
import {
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import AddressFieldPlaceholder from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/AddressFieldPlaceholder';
import TextBaseFieldContextMenu from '@app/features/apply-react-signing/components/FieldSetupStep/TextBaseFieldContextMenu';
import { useTransformTextStyle } from '@app/features/apply-react-signing/libs/hooks/useTransFormTextStyle';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './ConfigurableAddressField.scss';

export const ADDRESS_MAX_LENGTH = 125;

export function ConfigurableAddressFieldRenderer({
  restrictModification,
  readOnly,
  fieldId,
  scale,
  viewport,
  isDataLabelEditable,
  isDataLabelDuplicated,
  onFieldRemove,
}: ConfigurableAutofillFieldGroupRendererProps) {
  const [isResizing, setResizing] = useState(false);
  const [showContextMenu, toggleContextMenu] = useToggle(true);
  const [showGuideline, setShowGuideline] = useState(false);
  const {
    field,
    setPosition,
    setSize,
    setDataLabel,
    setAlign,
    setFontSize,
    setFontStyle,
    setRequired,
  } = useAddressField(fieldId);

  const fontSize = field?.config.fontStyle.size ?? 60;
  const fontPixelSize: number = PDFFontNormalizer.toPixelFontSize({ fontSize }, scale);

  const absoluteSize = toAbsoluteSize(field?.size ?? { width: 0, height: 0 }, viewport);
  const absolutePoint = toAbsolutePoint(field?.position ?? { x: 0, y: 0 }, viewport);

  // 'g', 'j', 'q' 등과 같이 폰트 높이 아래로 튀어 나오는 영역을 보정하기 위한 값
  const currentFontSize = getTextBaseFieldMinimumSize(fontSize, scale);
  const minimumFontSize = getTextBaseFieldMinimumSize(4, scale);

  const minSize: AbsoluteSize = createAbsoluteSize({
    width: (ADDRESS_MAX_LENGTH * minimumFontSize.width) / 3,
    height: Math.max(minimumFontSize.height * 3, currentFontSize.height),
  });

  const { transformTextStyle } = useTransformTextStyle({
    fontFamily: field?.config.fontStyle.type ?? 'NOTO_SANS',
    textAlign: field?.config.textAlign ?? 'LEFT',
    fontSize: field?.config.fontStyle.size ?? 12,
    size: absoluteSize,
    scale,
  });

  const dataLabelErrorMessage = useMemo(() => {
    if (!field?.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [field?.dataLabel, isDataLabelDuplicated]);

  const correctSize = () => {
    const relativeSize = toRelativeSize(
      getAddressFieldCorrectedSize({
        size: absoluteSize,
        scale,
        fontSize: field?.config.fontStyle.size ?? 60,
      }),
      viewport
    );
    setSize(relativeSize.width, relativeSize.height);
  };

  // 폰트크기가 변경되었을 때, 최소 크기와 글줄 길이에 맞게 보정합니다.
  useUpdateEffect(() => {
    correctSize();
  }, [fontPixelSize]);

  if (!field || field.participantReference.type !== 'SIGNER') {
    return null;
  }

  const handlePointChangeStart = () => {
    setShowGuideline(true);
    toggleContextMenu(true);
  };

  const handlePointChangeStop = () => {
    setShowGuideline(false);
  };

  const handlePointChange = (point: AbsolutePoint) => {
    const relativePoint = clampFieldPoint(toRelativePoint(point, viewport));
    setPosition(relativePoint.x, relativePoint.y);
    setShowGuideline(true);
  };

  const handleChangeSizeStart = () => {
    setResizing(true);
    setShowGuideline(true);
  };

  const handleChangeSize = (size: AbsoluteSize) => {
    const relativeSize = toRelativeSize(size, viewport);

    setShowGuideline(true);
    setSize(relativeSize.width, relativeSize.height);
  };

  const handleChangeSizeStop = () => {
    setResizing(false);
    setShowGuideline(false);
    correctSize();
  };

  const handleRemoveField = () => {
    onFieldRemove(fieldId);
  };

  const participantOrder = field.participantReference.index + 1;

  const configurableAddressFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableAddressField',
    participantOrder
  );

  const handleChangeFontSize = (fontSize: number) => {
    setFontSize(fontSize);
  };

  return (
    <FieldBase
      className={cx({
        ConfigurableAddressField: true,
        'ConfigurableAddressField--resizing': isResizing,
        [configurableAddressFieldClassByOrder]: true,
        'ConfigurableAddressField__disable-resize': readOnly || restrictModification,
      })}
      data-testid="address-field"
      dragCancelSelectors={['.ConfigurableAddressField__context-menu']}
      fieldGuideline={{
        guideLineOffset: {
          left: 2,
          top: 2,
          right: 1,
          bottom: 1,
        },
        viewport,
        showGuideLine: showGuideline,
      }}
      maxConstraints={[viewport.width - absolutePoint.x, viewport.height - absolutePoint.y]}
      minConstraints={[minSize.width, minSize.height]}
      point={absolutePoint}
      readOnly={readOnly}
      restrictModification={restrictModification}
      size={absoluteSize}
      snapSize={createAbsoluteSize({
        width: 1,
        height: fontPixelSize,
      })}
      onPointChange={handlePointChange}
      onPointChangeStart={handlePointChangeStart}
      onPointChangeStop={handlePointChangeStop}
      onSizeChange={handleChangeSize}
      onSizeChangeStart={handleChangeSizeStart}
      onSizeChangeStop={handleChangeSizeStop}
    >
      <Box height="100%" width="100%">
        <OutsideClickHandler
          onOutsideClick={() => {
            if (field.dataLabel && !isDataLabelDuplicated) {
              toggleContextMenu(false);
            }
          }}
        >
          {showContextMenu && !readOnly && !restrictModification && (
            <Box className="ConfigurableAddressField__context-menu">
              <TextBaseFieldContextMenu
                dataLabel={field.dataLabel}
                dataLabelErrorMessage={dataLabelErrorMessage}
                fontFamily={field.config.fontStyle.type}
                fontSize={field.config.fontStyle.size}
                isDataLabelEditable={isDataLabelEditable}
                isRequired={field.config.required}
                participantName={getSignerDisplayName(field.participantReference)}
                participantOrder={participantOrder}
                textAlign={field.config.textAlign}
                onDataLabelChange={(dataLabel) => setDataLabel(dataLabel)}
                onFontFamilyChange={(fontFamily) => setFontStyle(fontFamily)}
                onFontSizeChange={handleChangeFontSize}
                onRequiredToggle={(_, required) => setRequired(required)}
                onTextAlignChange={(align) => setAlign(align)}
              />
            </Box>
          )}
          <Box height="100%" position="absolute" width="100%">
            <AddressFieldPlaceholder
              fontPixelSize={fontPixelSize}
              participantOrder={participantOrder}
              star={field.config.required}
              style={transformTextStyle}
            />
            {!readOnly && !restrictModification && (
              <Box className="ConfigurableAddressField__x-button">
                <RoundedXButton onClick={handleRemoveField} />
              </Box>
            )}
          </Box>
        </OutsideClickHandler>
      </Box>
    </FieldBase>
  );
}
